/**
 * Archivo: OrlaColegial.js
 * Autor: Acher Egido Mairal
 * Fecha de última modificación: 2024-07-10
 * 
 * Descripción:
 * Este componente define la vista para la gestión de la orla colegial. Permite a los usuarios con permisos adecuados
 * visualizar la lista de colegiales, buscar, filtrar, añadir y eliminar colegiales, así como marcar y desmarcar favoritos.
 * 
 * Dependencias:
 * - react: ^18.2.0
 * - js-cookie: ^3.0.1
 * - Servicios/ColegialesServicios: Funciones para interactuar con el servicio de colegiales.
 * - Componentes/MetaTags: Componente para las etiquetas meta del documento.
 * - Componentes/Cabecera: Componente para la cabecera de la aplicación.
 * - Componentes/SegundaCabecera: Componente para la segunda cabecera de la aplicación.
 * - Componentes/Filtros: Componente para los filtros de búsqueda.
 * - Componentes/InfoColegial: Componente para la información detallada de un colegial.
 * - Componentes/VentanaAñadirColegiales: Componente para añadir nuevos colegiales.
 * 
 * Propósito:
 * Proveer una interfaz de usuario para la visualización y gestión de la orla colegial, incluyendo la capacidad
 * de buscar, filtrar, añadir y eliminar colegiales, así como marcar y desmarcar favoritos.
 */

import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import MetaTags from '../Componentes/MetaTags';
import Cabecera from '../Componentes/Cabecera';
import SegundaCabecera from '../Componentes/SegundaCabecera';
import Filtros from '../Componentes/Filtros';
import InfoColegial from '../Componentes/InfoColegial';
import VentanaAñadirColegiales from '../Componentes/VentanaAñadirColegiales';
import '../Estilos/estilos.css';
import { listaColegiales, removeFavorito, addFavorito, eliminarColegial } from '../Servicios/ColegialesServicios';

import logoCerbunaBlanco from '../Imagenes/Iconos/LogoCerbunaBlanco.png'
import filtroBlanco from '../Imagenes/Iconos/FiltroBlanco.png'
import lupaNegra from '../Imagenes/Iconos/LupaNegra.png'
import corazonRojo from '../Imagenes/Iconos/CorazonRojo.png'

function OrlaColegial() {
  const [colegiales, setColegiales] = useState([]);
  const [busqueda, setBusqueda] = useState('');

  const [selectedHombre, setSelectedHombre] = useState(false);
  const [selectedMujer, setSelectedMujer] = useState(false);
  const [selectedOtro, setSelectedOtro] = useState(false);
  const [selectedPrimera, setSelectedPrimera] = useState(false);
  const [selectedSegunda, setSelectedSegunda] = useState(false);
  const [selectedTercera, setSelectedTercera] = useState(false);
  const [selectedCuarta, setSelectedCuarta] = useState(false);
  const [selectedQuinta, setSelectedQuinta] = useState(false);
  const [selected100, setSelected100] = useState(false);
  const [selected200, setSelected200] = useState(false);
  const [selected300, setSelected300] = useState(false);
  const [selected400, setSelected400] = useState(false);
  const [selectedAdjuntos, setSelectedAdjuntos] = useState(false);
  const [selectedFavoritos, setSelectedFavoritos] = useState(false);

  const urlImagenes = `${process.env.REACT_APP_URL_IMAGENES}FotosColegiales/`
  //console.log("URL: ", urlImagenes)
  
  const [token, setToken] = useState('');
  const [permisos, setPermisos] = useState(null);
  const [hayVentanas, setHayVentanas] = useState(false);
  const [mostrarVentanaFiltros, setMostrarVentanaFiltros] = useState(false);
  const [mostrarInfoColegial, setMostrarInfoColegial] = useState(false);
  const [mostrarAñadirColegiales, setMostrarAñadirColegiales] = useState(false);
  const [ordenacion, setOrdenacion] = useState('nombre');

  // Estado para controlar el índice del colegial actual en el carrusel
  const [indiceColegialActual, setIndiceColegialActual] = useState(0);

  const handleAñadirColegiales = () => {
    setMostrarAñadirColegiales(true);
    setHayVentanas(true);
};
const handleCloseAñadirColegiales = () => {
    setMostrarAñadirColegiales(false);
    setHayVentanas(false);
};

  const handleFiltros = () => {
      setMostrarVentanaFiltros(true);
      setHayVentanas(true);
  };
  const handleCloseVentanaFiltros = () => {
      setMostrarVentanaFiltros(false);
      setHayVentanas(false);
  };

  // Función para abrir la información detallada de un colegial
  const handleMostrarInfoColegial = (index) => {
      setIndiceColegialActual(index)
      setMostrarInfoColegial(true);
      setHayVentanas(true);
  };
  // Función para cerrar la información detallada de un colegial
  const closeInfoColegial = () => {
      setMostrarInfoColegial(false);
      setHayVentanas(false);
  };

  const actualizarListaColegiales = async () => {
    //console.log("Entro a Actualizar lista colegiales")
    const resultado = await listaColegiales(token, permisos);
    //console.log('Resultado:', resultado)
    if (resultado && resultado.success) {
      setColegiales(resultado.data);
    } else {
      console.log(resultado ? resultado.message : 'Error desconocido');
    }
  };

  useEffect(() => {
    const obtenerDatos = async () => {
      const tokenNuevo = Cookies.get('token');
      const permisosNuevos = Cookies.get('permisos');
      setToken(tokenNuevo)
      setPermisos(permisosNuevos)
      //console.log("Token:", tokenNuevo, " Permisos:", permisosNuevos);
    };
    obtenerDatos();
    actualizarListaColegiales();
  }, [token]);

  const actualizarFavorito = (idColegial, esFavorito) => {
    setColegiales(colegiales.map(colegial => {
      if (colegial.id_colegial === idColegial) {
        return { ...colegial, esFavorito };
      }
      return colegial;
    }));
  };

  const manejarFavorito = async (idColegial, esFavoritoActual) => {
    const result = esFavoritoActual 
      ? await removeFavorito(token, idColegial)
      : await addFavorito(token, idColegial);
    if (result.success) {
      actualizarFavorito(idColegial, !esFavoritoActual);
    } else {
      console.error(result.message);
    }
  };

  const eliminarColegialEstado = async (idColegial) => {
    const result = await eliminarColegial(token, idColegial);
    if (result.success) {
      setColegiales(colegiales.filter(colegial => colegial.id_colegial !== idColegial));
    } else {
      console.error(result.message);
    }
    return result;
  };

  const CarruselColegiales = ({colegiales, indiceColegialActual }) => {
    const handlePrev = () => {
      setIndiceColegialActual(prevIndice => Math.max(0, prevIndice - 1));
    };
  
    const handleNext = () => {
      setIndiceColegialActual(prevIndice => Math.min(colegiales.length - 1, prevIndice + 1));
    };

    const buttonStyle = {
      position: 'absolute',
      top: '50%',
      zIndex: '5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width:'5rem',
      height: '5rem',
      borderRadius: '50%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      border: 'none',
      outline: 'none',
      cursor: 'pointer',
      transform: 'translateY(-50%)',
      color: '#000',
    };
    const leftButtonStyle = {
      ...buttonStyle,
      left: '3rem',
      display: indiceColegialActual === 0 ? 'none' : 'block',
    };
    const rightButtonStyle = {
      ...buttonStyle,
      right: '3rem',
      display: indiceColegialActual === colegiales.length - 1 ? 'none' : 'block',
    };
    const handleMouseEnter = (event) => {
      event.target.style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
    };
    const handleMouseLeave = (event) => {
      event.target.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    };

    return (
      <div id="carrusel" className="carousel-container" style={{ width: '100%', zIndex:'3', height:'100%'}}>
        <div className="carousel-inner" style={{height:'100%', backgroundColor:'red'}} >
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <InfoColegial 
                onClose={closeInfoColegial} 
                permisos={permisos} 
                colegial={colegiales[indiceColegialActual]}
                manejarFavorito={manejarFavorito}
                actualizarLista={actualizarListaColegiales}
                eliminarColegial={eliminarColegialEstado}
                />
            </div>
        </div>
        <button style={leftButtonStyle} type="button" onClick={handlePrev} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <span className="carousel-control-prev-icon" aria-hidden="true"></span>
        </button>
        <button style={rightButtonStyle} type="button" onClick={handleNext} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <span className="carousel-control-next-icon" aria-hidden="true"></span>
        </button>
      </div>
    );
  };

  const renderColegiales = (colegialesFiltrados) => {
    if (colegialesFiltrados.length === 0) {
      return <div className="d-flex container-fluid text-black">No hay ningún colegial filtrado</div>;
    }
    const truncateText = (text, maxLength) => {
      return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
    };
    return colegialesFiltrados.map((colegial, index) => (
      <div key={index} className="card mr-4" style={{ backgroundColor: 'black', flex: '0 0 auto', minWidth: '14rem', width:'14rem', 
      cursor:'pointer', maxHeight:'25rem', position: 'relative'}} onClick={() => handleMostrarInfoColegial(index)}>
        <div className="row-fluid d-flex justify-content-center" style={{backgroundColor:'white'}}>
          <img alt="login-icon" className="card-img-top" src={urlImagenes + colegial.foto_colegial} style={{height:'9rem', width:'14rem'}} />
          {colegial.esFavorito && (
            <img alt="corazon" src={corazonRojo} style={{ position: 'absolute', top: '0rem', right: '0rem', height:'2rem', width:'2rem'}} />
          )}
        </div>
        <div className="card-body text-center" style={{ marginTop: '-0.3rem' }}>
          <h5 className="card-title customFontSizeTextoMediano text-white">{colegial.nombre_colegial}</h5>
          <h5 className="card-title customFontSizeTextoMediano text-white">{truncateText(colegial.apellidos_colegial, 15)}</h5>
          <span className="text-white customFontSizeTextoPequeño">{truncateText(colegial.estudios_colegial, 15)}</span>
          <div className='row p-2 justify-content-center' style={{height:'8rem'}}>
            <div className='container'>
              {(permisos === "Administrador" || permisos === "Conserjería" || colegial.tieneCargo_colegial) && (
                <>
                  <h5 className="mt-2 customFontSizeTextoMuyPequeño text-white">
                    Habitación: {colegial.habitacion_colegial}
                  </h5>
                </>
              )}
              {colegial.tieneCargo_colegial && (
                <img
                  alt="login-icon"
                  className="img mt-2"
                  src={logoCerbunaBlanco}
                  style={{ height: '5rem', width: '5rem' }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    ))
  };
  

  const renderColegialesMovil = (colegialesFiltrados) => {
    return colegialesFiltrados.map((colegial, index) => (
      <div key={index} className="d-flex align-items-center col-12 p-1" style={{ backgroundColor: 'black', height: '8rem', cursor: 'pointer',
        padding: '0.5rem', color: 'white',borderBottom: '1px solid #333', position: 'relative'}} onClick={() => handleMostrarInfoColegial(index)}>
        <div className="d-flex justify-content-center col-3 ml-1" style={{ marginRight: '0.5rem' }}>
          <img
            alt="colegial-foto"
            src={urlImagenes + colegial.foto_colegial}
            style={{ height: '6rem', width: '6rem', objectFit: 'cover', borderRadius: '50%' }}
          />
        </div>
        <div className='col-6 p-0 ml-2'>
          <div className="flex-grow-1">
            <div>
              <span className="mb-1 customFontSizeTextoMediano" style={{ fontWeight: 'bold' }}>{colegial.nombre_colegial} {colegial.apellidos_colegial} </span>
            </div>
            <div>
              <span className="mb-1 customFontSizeTextoPequeño">{colegial.estudios_colegial}</span>
            </div>
            <div>
              {(permisos === "Administrador" || permisos === "Conserjería" || colegial.tieneCargo_colegial) && (
                <span className="mb-1 customFontSizeTextoPequeño">Habitación: {colegial.habitacion_colegial}</span>
              )}
            </div>
          </div>
        </div>
        <div className='col-2 d-flex p-0'>
          <div className=" col-1 d-flex" style={{ marginLeft: '-2rem' }}>
            {colegial.tieneCargo_colegial && (
              <img alt="tieneCargo" src={logoCerbunaBlanco} style={{ height: '2.5rem', width: '2.5rem', cursor: 'pointer', marginTop: '0.3rem', marginLeft: '0.3rem' }} />
            )}
          </div>
        </div>
        {colegial.esFavorito && (
          <img alt="corazon" src={corazonRojo} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', height:'2rem', width:'2rem'}} />
        )}
      </div>
    ))
  };
  

  // Filtrado de colegiales según el término de búsqueda
  const colegialesFiltrados = colegiales.filter(colegial => {

    // Filtrado por término de búsqueda
    const matchesBusqueda = colegial.nombre_colegial.toLowerCase().includes(busqueda.toLowerCase()) ||
      colegial.apellidos_colegial.toLowerCase().includes(busqueda.toLowerCase());
    // Filtrado por género
    const matchesGenero = (
      (!selectedHombre && !selectedMujer && !selectedOtro) || 
      (selectedHombre && colegial.genero_colegial === 'Hombre') ||
      (selectedMujer && colegial.genero_colegial === 'Mujer') ||
      (selectedOtro && colegial.genero_colegial === 'Otro')
    );
    // Filtrado por promoción
    const matchesPromocion = (
      (!selectedPrimera && !selectedSegunda && !selectedTercera && !selectedCuarta && !selectedQuinta) || 
      (selectedPrimera && colegial.promocion_colegial === 'Primera') ||
      (selectedSegunda && colegial.promocion_colegial === 'Segunda') ||
      (selectedTercera && colegial.promocion_colegial === 'Tercera') ||
      (selectedCuarta && colegial.promocion_colegial === 'Cuarta') ||
      (selectedQuinta && colegial.promocion_colegial === 'Quinta o más')
    );
    // Filtrado por pasillo
    const matchesPasillo = (
      (!selected100 && !selected200 && !selected300 && !selected400) || 
      (selected100 && colegial.pasillo_colegial === '100s') ||
      (selected200 && colegial.pasillo_colegial === '200s') ||
      (selected300 && colegial.pasillo_colegial === '300s') ||
      (selected400 && colegial.pasillo_colegial === '400s')
    );
    // Filtrado por adjuntos
    const matchesAdjuntos = !selectedAdjuntos || colegial.tieneCargo_colegial;
    // Filtrado por favoritos
    const matchesFavoritos = !selectedFavoritos || colegial.esFavorito;
    // Devolver true si se cumple el término de búsqueda y al menos uno de los otros filtros
    return matchesBusqueda && matchesGenero && matchesPromocion && matchesPasillo && matchesAdjuntos && matchesFavoritos;
  }).sort((a, b) => {
    if (busqueda) {
        const aMatchesExact = a.nombre_colegial.toLowerCase() === busqueda.toLowerCase();
        const bMatchesExact = b.nombre_colegial.toLowerCase() === busqueda.toLowerCase();
        if (aMatchesExact && !bMatchesExact) return -1;
        if (!aMatchesExact && bMatchesExact) return 1;
    }

    switch (ordenacion) {
        case 'nombre':
            return a.nombre_colegial.localeCompare(b.nombre_colegial);
        case 'apellido':
            return a.apellidos_colegial.localeCompare(b.apellidos_colegial);
        case 'promocion':
            const promociones = ['Primera', 'Segunda', 'Tercera', 'Cuarta', 'Quinta o más'];
            return promociones.indexOf(a.promocion_colegial) - promociones.indexOf(b.promocion_colegial);
        default:
            return 0;
    }
});

  return (
  <> 
  <MetaTags /> 
    <div className="fondo">
        {/**CABECERA*/}
        <Cabecera permisos={permisos}/>
        {/** PANTALLA GRANDE */}
        <div className='d-none d-md-block '>
            {/**SEGUNDA CABECERA */}
            <SegundaCabecera hayVentanas={hayVentanas} permisos={permisos}  />
            {mostrarInfoColegial && <CarruselColegiales colegiales={colegialesFiltrados} indiceColegialActual={indiceColegialActual}/>}
            {mostrarAñadirColegiales && <VentanaAñadirColegiales onClose={handleCloseAñadirColegiales} actualizarLista={actualizarListaColegiales} />}
            {mostrarVentanaFiltros &&   <Filtros 
              onClose={handleCloseVentanaFiltros} 
              selectedHombre={selectedHombre} setSelectedHombre={setSelectedHombre}
              selectedMujer={selectedMujer} setSelectedMujer={setSelectedMujer}
              selectedOtro={selectedOtro} setSelectedOtro={setSelectedOtro}
              selectedPrimera={selectedPrimera} setSelectedPrimera={setSelectedPrimera}
              selectedSegunda={selectedSegunda} setSelectedSegunda={setSelectedSegunda}
              selectedTercera={selectedTercera} setSelectedTercera={setSelectedTercera}
              selectedCuarta={selectedCuarta} setSelectedCuarta={setSelectedCuarta}
              selectedQuinta={selectedQuinta} setSelectedQuinta={setSelectedQuinta}
              selected100={selected100} setSelected100={setSelected100}
              selected200={selected200} setSelected200={setSelected200}
              selected300={selected300} setSelected300={setSelected300}
              selected400={selected400} setSelected400={setSelected400}
              selectedAdjuntos={selectedAdjuntos} setSelectedAdjuntos={setSelectedAdjuntos}
              selectedFavoritos={selectedFavoritos} setSelectedFavoritos={setSelectedFavoritos}
              ordenacion={ordenacion} setOrdenacion={setOrdenacion}
            />}
            {/**COLEGIALES*/}
            <div className='container-fluid justify-content-center p-2 mt-2'>
              <div className='container-fluid d-flex justify-content-center p-2'>
                <form className="col-6 d-flex position-relative mt-1" role="search" >
                  <div className="input-group">
                  <input
                      aria-label="Search"
                      className="form-control me-2"
                      placeholder="Buscar"
                      style={{ borderRadius: '2rem', border: '1px solid black', height: '2rem' }}
                      type="search"
                      value={busqueda} 
                      onChange={(e) => setBusqueda(e.target.value)}
                  />
                      <button className="btn btn-outline-success" style={{ backgroundColor: 'transparent', border: 'none', bottom: '0', marginTop: '-0.2rem',  position: 'absolute', right: '0', top: '0', zIndex: '3'}} 
                      onClick={(e) => e.preventDefault()} >
                      <img
                          alt="Buscar"
                          src={lupaNegra}
                          style={{ height: '1.3rem', width: '1.3rem' }}
                      />
                      </button>
                  </div>
                </form>
                <div className="col-xl-1 col-lg-1 col-md-1">
                  <button className="rounded-circle btn" style={{ backgroundColor: '#3C3C3C', border: '1px solid black' }} type="submit" onClick={() => handleFiltros()}> 
                    <img
                      alt="inicia-sesion"
                      className="img-fluid "
                      src={filtroBlanco}
                      style={{ height: '1.7rem', minWidth:'1.7rem'}}
                    />
                  </button>
                </div>
                {permisos === "Administrador" && 
                <div className="col-xl-1 col-lg-1 col-md-1">
                  <button className="btn" style={{ backgroundColor: '#3C3C3C', border: '1px solid black' }} type="submit" onClick={() => handleAñadirColegiales()}> 
                    <span className='text-white'>Añadir colegiales</span>
                  </button>
                </div>}
              </div>
                <div className='container-fluid p-4' style={{overflowY: 'auto', height:'calc(100vh - 13rem)',  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '3rem', opacity: hayVentanas ? 0.4 : 1 }}>
                  {renderColegiales(colegialesFiltrados)}
                </div>
            </div>
        </div>
        {/**PARA PANTALLAS PEQUEÑAS*/}
        <div className='d-block d-md-none p-0'>
            {mostrarInfoColegial && <CarruselColegiales colegiales={colegialesFiltrados} indiceColegialActual={indiceColegialActual}/>}
            {mostrarVentanaFiltros &&   <Filtros 
              onClose={handleCloseVentanaFiltros} 
              selectedHombre={selectedHombre} setSelectedHombre={setSelectedHombre}
              selectedMujer={selectedMujer} setSelectedMujer={setSelectedMujer}
              selectedOtro={selectedOtro} setSelectedOtro={setSelectedOtro}
              selectedPrimera={selectedPrimera} setSelectedPrimera={setSelectedPrimera}
              selectedSegunda={selectedSegunda} setSelectedSegunda={setSelectedSegunda}
              selectedTercera={selectedTercera} setSelectedTercera={setSelectedTercera}
              selectedCuarta={selectedCuarta} setSelectedCuarta={setSelectedCuarta}
              selectedQuinta={selectedQuinta} setSelectedQuinta={setSelectedQuinta}
              selected100={selected100} setSelected100={setSelected100}
              selected200={selected200} setSelected200={setSelected200}
              selected300={selected300} setSelected300={setSelected300}
              selected400={selected400} setSelected400={setSelected400}
              selectedAdjuntos={selectedAdjuntos} setSelectedAdjuntos={setSelectedAdjuntos}
              selectedFavoritos={selectedFavoritos} setSelectedFavoritos={setSelectedFavoritos}
            />}
            {/**COLEGIALES*/}
            <div className='container-fluid justify-content-center p-2 mt-2'>
              <div className='container-fluid d-flex justify-content-center p-2'>
                <form className="col-9 d-flex position-relative mt-1" role="search" >
                  <div className="input-group">
                  <input
                      aria-label="Search"
                      className="form-control me-2"
                      placeholder="Buscar"
                      style={{ borderRadius: '2rem', border: '1px solid black', height: '2rem' }}
                      type="search"
                      value={busqueda} 
                      onChange={(e) => setBusqueda(e.target.value)}
                  />
                      <button className="btn btn-outline-success" style={{ backgroundColor: 'transparent', border: 'none', bottom: '0', marginTop: '-0.2rem',  position: 'absolute', right: '0', top: '0', zIndex: '3'}} 
                      onClick={(e) => e.preventDefault()} >
                      <img
                          alt="Buscar"
                          src={lupaNegra}
                          style={{ height: '1.3rem', width: '1.3rem' }}
                      />
                      </button>
                  </div>
                </form>
                <div className="col-xl-1 col-lg-1 col-md-1">
                  <button className="rounded-circle btn" style={{ backgroundColor: '#3C3C3C', border: '1px solid black' }} type="submit" onClick={() => handleFiltros()}> 
                    <img
                      alt="inicia-sesion"
                      className="img-fluid "
                      src={filtroBlanco}
                      style={{ height: '1.7rem', minWidth:'1.7rem'}}
                    />
                  </button>
                </div>
              </div>
              <div className='w-100' style={{ overflowY: 'auto', height: 'calc(100vh - 8.5rem)', display: 'grid', gap: '0', opacity: hayVentanas ? 0.4 : 1 }}>
                {renderColegialesMovil(colegialesFiltrados)}
              </div>

            </div>
        </div>
        {/** Pantalla de overlay para que no se pueda seleccionar ningún objeto o interactuar si hay alguna ventana */}
        {hayVentanas && (
          <div className="overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 2
          }}></div>
        )}
    </div>
    </>
    );
}

export default OrlaColegial;
